import React, { createContext, useContext, useState, useEffect } from "react";
import AuthContext from "./AuthContext";

const IntroductionContext = createContext();
export default IntroductionContext;

export const IntroductionProvider = ({ children }) => {
    const [introduction, setIntroduction] = useState(null);
    const [loading, setLoading] = useState(true);
    const { authTokens, BASE_URL } = useContext(AuthContext);

    // Fetch introduction data
    const getIntroduction = async () => {
        try {
            const response = await fetch(`${BASE_URL}/api/get_introduction/`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setIntroduction(data);
            } else {
                console.error("Failed to fetch introduction:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching introduction:", error);
        } finally {
            setLoading(false);
        }
    };

    // Update introduction data
    const updateIntroduction = async (introductionData) => {
        try {
            const response = await fetch(`${BASE_URL}/api/update_introduction/`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": "Bearer " + String(authTokens?.access)
                },
                body: JSON.stringify(introductionData)
            });

            if (response.ok) {
                const data = await response.json();
                setIntroduction(data);
                return { success: true, data };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to update introduction");
            }
        } catch (error) {
            console.error("Error updating introduction:", error);
            return { success: false, error: error.message };
        }
    };

    // Upload introduction images
    const uploadIntroductionImages = async (leftImageFile, rightImageFile) => {
        try {
            const formData = new FormData();
            if (leftImageFile) formData.append('left_image', leftImageFile);
            if (rightImageFile) formData.append('right_image', rightImageFile);

            const response = await fetch(`${BASE_URL}/api/upload_introduction_images/`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: {
                    "Authorization": "Bearer " + String(authTokens?.access)
                },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                
                // Upload files to S3 using presigned URLs
                if (leftImageFile && data.left_image_upload_url) {
                    await uploadToS3(data.left_image_upload_url, leftImageFile);
                }
                if (rightImageFile && data.right_image_upload_url) {
                    await uploadToS3(data.right_image_upload_url, rightImageFile);
                }
                
                // Refresh introduction data to get updated image URLs
                await getIntroduction();
                
                return { success: true };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to upload images");
            }
        } catch (error) {
            console.error("Error uploading introduction images:", error);
            return { success: false, error: error.message };
        }
    };

    const uploadToS3 = async (presignedUrl, file) => {
        const response = await fetch(presignedUrl, {
            method: "PUT",
            body: file,
            headers: {
                'Content-Type': file.type,
            },
        });
        
        if (!response.ok) {
            throw new Error('Failed to upload image to S3');
        }
    };

    // Load introduction data on mount
    useEffect(() => {
        getIntroduction();
    }, []);

    const value = {
        introduction,
        loading,
        getIntroduction,
        updateIntroduction,
        uploadIntroductionImages
    };

    return (
        <IntroductionContext.Provider value={value}>
            {children}
        </IntroductionContext.Provider>
    );
}; 